/**
 * Transaction Rules Service
 * CRUD operations and real-time subscriptions for per-user transaction rules.
 * Rules auto-categorize transactions as Need/Want/Regret at display-time.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type {
  TransactionRule,
  TransactionRuleAction,
  RuleMatchField,
  RuleMatchType,
} from '../../types';

const USER_CONTENT_COLLECTION = 'userContent';
const RULES_SUBCOLLECTION = 'transactionRules';

const getRulesCollectionRef = (uid: string) => {
  return collection(db, USER_CONTENT_COLLECTION, uid, RULES_SUBCOLLECTION);
};

const getRuleDocRef = (uid: string, ruleId: string) => {
  return doc(db, USER_CONTENT_COLLECTION, uid, RULES_SUBCOLLECTION, ruleId);
};

const generateRuleId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `rule_${timestamp}_${random}`;
};

const utcIso = (): string => new Date().toISOString();

/**
 * Delete existing rules that would conflict with a new rule.
 * For each merchant match (same matchField + matchValue + matchType),
 * there should be at most one reflection rule and one category rule.
 */
const deleteConflictingRules = async (
  uid: string,
  matchField: RuleMatchField,
  matchValue: string,
  matchType: RuleMatchType,
  actions: TransactionRuleAction,
): Promise<void> => {
  const hasReflection = actions.autoReflection !== undefined;
  const hasCategory = actions.categoryOverride !== undefined || actions.categoryDetailedOverride !== undefined;
  if (!hasReflection && !hasCategory) return;

  const rulesRef = getRulesCollectionRef(uid);
  const q = query(
    rulesRef,
    where('matchField', '==', matchField),
    where('matchValue', '==', matchValue.toLowerCase()),
    where('matchType', '==', matchType),
  );
  const snapshot = await getDocs(q);

  const deletes: Promise<void>[] = [];
  for (const d of snapshot.docs) {
    const existing = d.data() as TransactionRule;
    const existingHasReflection = existing.actions?.autoReflection !== undefined;
    const existingHasCategory = existing.actions?.categoryOverride !== undefined || existing.actions?.categoryDetailedOverride !== undefined;

    if ((hasReflection && existingHasReflection) || (hasCategory && existingHasCategory)) {
      deletes.push(deleteDoc(getRuleDocRef(uid, d.id)));
    }
  }
  await Promise.all(deletes);
};

/**
 * Create a new transaction rule
 */
export const createTransactionRule = async (
  uid: string,
  matchField: RuleMatchField,
  matchValue: string,
  matchType: RuleMatchType,
  actions: TransactionRuleAction,
): Promise<TransactionRule> => {
  // Remove conflicting rules before creating the new one
  await deleteConflictingRules(uid, matchField, matchValue, matchType, actions);

  const ruleId = generateRuleId();
  const now = utcIso();

  const rule: TransactionRule = {
    id: ruleId,
    uid,
    matchField,
    matchValue: matchValue.toLowerCase(),
    matchType,
    actions,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(getRuleDocRef(uid, ruleId), rule);

  // Batch-apply the rule to existing unreflected transactions (fire-and-forget)
  if (actions.autoReflection) {
    httpsCallable(functions, 'enough_apply_transaction_rule')({ ruleId })
      .catch((err) => console.warn('Failed to batch-apply rule:', err));
  }

  return rule;
};

/**
 * Get all rules for a user (one-time fetch)
 */
export const getUserTransactionRules = async (uid: string): Promise<TransactionRule[]> => {
  const rulesRef = getRulesCollectionRef(uid);
  const q = query(rulesRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(d => ({
    ...d.data(),
    id: d.id,
  })) as TransactionRule[];
};

/**
 * Update an existing rule (partial update)
 */
export const updateTransactionRule = async (
  uid: string,
  ruleId: string,
  updates: Partial<Pick<TransactionRule, 'matchValue' | 'matchType' | 'actions' | 'isActive'>>,
): Promise<void> => {
  const patch: Record<string, unknown> = { ...updates, updatedAt: utcIso() };
  if (updates.matchValue !== undefined) {
    patch.matchValue = updates.matchValue.toLowerCase();
  }
  await updateDoc(getRuleDocRef(uid, ruleId), patch);
};

/**
 * Delete a rule permanently and revert its rule-applied reflections
 */
export const deleteTransactionRule = async (uid: string, ruleId: string): Promise<void> => {
  // Read the rule before deleting so we can revert its effects
  const ruleDoc = await getDoc(getRuleDocRef(uid, ruleId));
  const ruleData = ruleDoc.exists() ? ruleDoc.data() as TransactionRule : null;

  await deleteDoc(getRuleDocRef(uid, ruleId));

  // Revert rule-applied reflections for this merchant (fire-and-forget)
  if (ruleData?.actions?.autoReflection && ruleData.matchValue) {
    httpsCallable(functions, 'enough_revert_transaction_rule')({
      matchValue: ruleData.matchValue,
      matchType: ruleData.matchType || 'contains',
    }).catch((err) => console.warn('Failed to revert rule reflections:', err));
  }
};

/**
 * Subscribe to all rules for a user (real-time)
 */
export const subscribeToTransactionRules = (
  uid: string,
  callback: (rules: TransactionRule[]) => void,
): Unsubscribe => {
  const rulesRef = getRulesCollectionRef(uid);
  const q = query(rulesRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const rules = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id,
      })) as TransactionRule[];
      callback(rules);
    },
    (error) => {
      console.error('Error subscribing to transaction rules:', error);
      callback([]);
    },
  );
};
